import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getWorkOrder,
  resetWorkOrder,
  updateWorkOrder,
} from "../../../../redux/actions/workOrderActions";
import { getReviewStages, resetReviewStages } from "../../../../redux/actions/reviewStagesActions";
import { errorMessage } from "../../../../redux/actions/messagesActions";
import {
  selectBreadcrumbInfo,
  selectExecutionBriefId,
  selectHierarchyDetails,
  selectReviewers,
  selectWorkOrder,
  selectWorkOrderContentCreatorDetails,
  selectWorkOrderFormErrors,
  selectWorkOrderId,
  selectWorkOrderName,
  selectWorkOrderNdaData,
  selectWorkOrderOwnerId,
  selectWorkOrderOwnerName,
  selectWorkOrderResourceType,
  selectWorkOrderStatus,
  selectWorkOrderUpdatedTime,
  selectWorkOrdersErrors,
  selectWorkOrdersLoader,
} from "../../../../redux/selectors/workOrders";
import * as userStore from "../../../../features/user/store/user.store";
import { BriefLoader } from "../../../../components/brief/BriefLoader";
import { BriefType } from "../../../../constants/types";
import { BriefWrapper } from "../../../../components/brief/brief-wrapper/brief-wrapper.component";
import { FileDownload } from "../../../../features/file-attachments/file-download/components/file-download/file-download-component";
import { IExtraAction } from "../../../../features/brief-header/types/iextra-action.interface";
import { TPageType } from "../../../../types/common";
import { calculatePermissions } from "../../../../types/user/permissions/ipermissions/ipermissions.functions";
import { errorMessageForDatesValidation } from "../../../../utils/validationUtils";
import { selectPageOwnerId, selectPageStatus } from "../../../../redux/selectors/root";
import { setPermissions } from "../../../../redux/actions/permissionsActions";

import { exportWorkOrderEndpoint } from "../../services/work-order.service";
import { getWorkOrderFormDefaultValues } from "../../types/work-order-form.functions";
import { getWorkOrderTemplateValidationSchema } from "../../types/work-order-template.functions";

import { WorkOrderForm } from "../work-order-form/work-order-form.component";

export const WorkOrder = () => {
  const pageType: TPageType = BriefType.WORK_ORDER;

  const dispatch = useDispatch();
  const executionBriefId = useSelector(selectExecutionBriefId);
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const brief = useSelector(selectWorkOrder);

  const breadcrumbInfo = useSelector(selectBreadcrumbInfo);
  const briefId = useSelector(selectWorkOrderId);
  const briefOwner = useSelector(selectWorkOrderOwnerName);
  const errors = useSelector(selectWorkOrdersErrors);
  const formErrors = useSelector(selectWorkOrderFormErrors);
  const isLoading = useSelector(selectWorkOrdersLoader);
  const ndaData = useSelector(selectWorkOrderNdaData);
  const status = useSelector(selectWorkOrderStatus);
  const updatedTime = useSelector(selectWorkOrderUpdatedTime);
  const workOrderName = useSelector(selectWorkOrderName);
  const workOrderTemplate = useSelector(selectWorkOrderResourceType);

  const validationSchema = useMemo(() => getWorkOrderTemplateValidationSchema(workOrderTemplate), [
    workOrderTemplate,
  ]);

  const userId = useSelector(userStore.getUserId);
  const userGroups = useSelector(userStore.getUserGroup);
  const workorderOwnerId = useSelector(selectWorkOrderOwnerId);
  const pageStatus = useSelector(selectPageStatus(pageType));
  const pageOwnerId = useSelector(selectPageOwnerId(pageType));
  const reviewers = useSelector(selectReviewers);
  const hierarchyDetails = useSelector(selectHierarchyDetails);
  const contentCreators = useSelector(selectWorkOrderContentCreatorDetails);

  const merchOwners = hierarchyDetails && hierarchyDetails.executionBriefMerchOwners;
  const briefOwnerId = hierarchyDetails.campaignBriefOwnerId;
  const executionOwnerId = hierarchyDetails.executionBriefOwnerId;

  const [permissionsCreated, setPermissionsCreated] = useState<boolean>(false);

  const defaultValues = useMemo(() => getWorkOrderFormDefaultValues(brief), [brief]);

  useEffect(
    () => () => {
      dispatch(resetWorkOrder());
      dispatch(resetReviewStages());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  useEffect(() => {
    if (id) {
      dispatch(getWorkOrder(id));
      dispatch(getReviewStages(Number(id), pageType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setPermissionsCreated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageStatus]);

  useEffect(() => {
    if (briefId) {
      const permissionContext = {
        userId,
        userGroups,
        pageType,
        briefOwnerId,
        executionOwnerId,
        workorderOwnerId,
        pageStatus,
        pageOwnerId,
        reviewers,
        parentReviewers: [],
        merchOwners,
        contentCreators,
      };

      const newPermissions = calculatePermissions(permissionContext);
      setPermissionsCreated(true);
      dispatch(setPermissions(newPermissions));
    }
  }, [
    briefId,
    dispatch,
    permissionsCreated,
    userId,
    userGroups,
    pageType,
    briefOwnerId,
    executionOwnerId,
    workorderOwnerId,
    pageStatus,
    pageOwnerId,
    reviewers,
    merchOwners,
    contentCreators,
  ]);

  const extraActions = (): IExtraAction[] => {
    return [
      {
        label: (
          <FileDownload
            url={exportWorkOrderEndpoint(briefId)}
            authRequired
            saveAsFileName="work-order-export.docx"
          >
            Export
          </FileDownload>
        ),
      },
    ];
  };

  const onSubmit = (data: any) => {
    const datesValidationError = errorMessageForDatesValidation(
      data.startAt,
      data.endAt,
      status,
      data.status
    );
    if (datesValidationError !== null) {
      dispatch(errorMessage(datesValidationError));
    } else {
      setIsSaving(true);
      dispatch(
        updateWorkOrder({
          executionBrief: executionBriefId,
          id: briefId,
          workOrderName,
          workOrderTemplate,
          ...data,
        })
      );
    }
  };

  return (
    <>
      <BriefLoader showIf={!Boolean(briefId)} />
      <BriefWrapper
        showIf={Boolean(briefId)}
        ContentComponent={WorkOrderForm}
        breadcrumbInfo={breadcrumbInfo}
        briefId={briefId}
        briefType={BriefType.WORK_ORDER}
        defaultValues={defaultValues}
        extraActions={extraActions()}
        formErrors={formErrors}
        isLoading={isLoading}
        isSaving={isSaving}
        name={workOrderName}
        ndaData={ndaData}
        onSubmit={onSubmit}
        owner={briefOwner}
        setIsSaving={setIsSaving}
        stateErrors={errors}
        status={status}
        template={workOrderTemplate}
        updatedTime={updatedTime}
        validationSchema={validationSchema}
      />
    </>
  );
};
